import { useState, useEffect, useMemo, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parse } from 'date-fns';
import { apiPost } from '../services/apiClient';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// Which prompts actually changed their answer day to day — a rating move of
// >= minDelta for the subject brand, or the brand entering/leaving the answer
// entirely — vs the aggregate line just drifting. Each rating move carries its
// exact contribution (in score points) to that day's unsmoothed daily score,
// so a dip can be attributed to specific prompts by reading down the column.
const DELTA_OPTIONS = [2, 3, 4, 5];
const TYPE_FILTERS = [
    { key: 'all', label: 'All changes' },
    { key: 'rating_move', label: 'Rating moves' },
    { key: 'presence', label: 'In / out' },
];
const INITIAL_VISIBLE = 25;

const fmtDay = (d) => format(parse(d, 'yyyy-MM-dd', new Date()), 'MMM d');

// One mover's change, phrased for the row.
const ChangeCell = ({ m }) => {
    if (m.change_type === 'appeared') {
        return <span className="llm-mover-change appeared">Appeared ({m.rating})</span>;
    }
    if (m.change_type === 'disappeared') {
        return <span className="llm-mover-change disappeared">Dropped out (was {m.prev_rating})</span>;
    }
    const up = Number(m.delta) > 0;
    return (
        <span className={`llm-mover-change ${up ? 'up' : 'down'}`}>
            {m.prev_rating} → {m.rating} ({up ? '+' : ''}{m.delta})
        </span>
    );
};

// Expanded row: the prompt's full trajectory in the visible range.
const PromptSeriesChart = ({ rows }) => {
    const chart = useMemo(() => {
        if (!rows || rows.length === 0) return null;
        const labels = rows.map(r => r.day);
        return {
            data: {
                labels,
                datasets: [
                    {
                        label: 'Rating',
                        data: rows.map(r => (r.present ? Number(r.rating) : null)),
                        borderColor: '#0057FF',
                        backgroundColor: 'rgba(0, 87, 255, 0.1)',
                        borderWidth: 2,
                        tension: 0.1,
                        pointRadius: 3,
                        spanGaps: false, // a gap IS the signal: the brand vanished
                    },
                    {
                        label: 'Not mentioned',
                        data: rows.map(r => (r.present ? null : 0)),
                        borderColor: '#E63946',
                        backgroundColor: '#E63946',
                        pointStyle: 'crossRot',
                        pointRadius: 5,
                        pointBorderWidth: 2,
                        showLine: false,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { min: 0, max: 10, title: { display: true, text: 'Rating (0–10)' } },
                    x: { ticks: { maxTicksLimit: 10 } },
                },
                plugins: {
                    legend: { display: true, labels: { boxWidth: 12, font: { size: 11 } } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const r = rows[ctx.dataIndex];
                                if (!r.present) return 'Not mentioned in this answer';
                                const bits = [`Rating: ${r.rating}`];
                                if (r.list_position != null) bits.push(`position ${r.list_position}`);
                                if (r.confidence != null) bits.push(`confidence ${r.confidence}`);
                                if (r.brand_mentions) bits.push(r.brand_mentions);
                                return bits.join(' · ');
                            },
                        },
                    },
                },
            },
        };
    }, [rows]);

    if (!chart) return <div className="llm-mover-series-empty">No asks of this prompt in the selected period.</div>;
    return (
        <div className="llm-mover-series-chart">
            <Line data={chart.data} options={chart.options} />
        </div>
    );
};

const LLMPromptMovers = ({ topicId, startDate, endDate, promptType, persona, onViewAnswers }) => {
    const [movers, setMovers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [minDelta, setMinDelta] = useState(3);
    const [typeFilter, setTypeFilter] = useState('all');
    const [visible, setVisible] = useState(INITIAL_VISIBLE);

    const [expandedKey, setExpandedKey] = useState(null);
    const [seriesByPrompt, setSeriesByPrompt] = useState({});
    const [seriesLoading, setSeriesLoading] = useState(false);

    useEffect(() => {
        if (!topicId) return;
        let cancelled = false;
        setIsLoading(true);
        setExpandedKey(null);
        setSeriesByPrompt({});
        setVisible(INITIAL_VISIBLE);

        apiPost('topic/llm-recommendations/prompt-movers', {
            topic_id: topicId,
            start_date: startDate,
            end_date: endDate,
            min_delta: minDelta,
            prompt_type: promptType,
            persona,
        })
            .then(r => r.json())
            .then(result => { if (!cancelled) setMovers(Array.isArray(result?.data) ? result.data : []); })
            .catch(err => {
                console.error('[LLMPromptMovers] movers error:', err);
                if (!cancelled) setMovers([]);
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });

        return () => { cancelled = true; };
    }, [topicId, startDate, endDate, promptType, persona, minDelta]);

    const filtered = useMemo(() => {
        if (typeFilter === 'rating_move') return movers.filter(m => m.change_type === 'rating_move');
        if (typeFilter === 'presence') return movers.filter(m => m.change_type !== 'rating_move');
        return movers;
    }, [movers, typeFilter]);

    const toggleExpand = useCallback((m) => {
        const key = `${m.prompt_id}-${m.day}`;
        if (expandedKey === key) {
            setExpandedKey(null);
            return;
        }
        setExpandedKey(key);
        if (!seriesByPrompt[m.prompt_id]) {
            setSeriesLoading(true);
            apiPost('topic/llm-recommendations/prompt-series', {
                topic_id: topicId,
                prompt_id: m.prompt_id,
                start_date: startDate,
                end_date: endDate,
            })
                .then(r => r.json())
                .then(result => {
                    setSeriesByPrompt(prev => ({ ...prev, [m.prompt_id]: result?.data || [] }));
                })
                .catch(err => console.error('[LLMPromptMovers] series error:', err))
                .finally(() => setSeriesLoading(false));
        }
    }, [expandedKey, seriesByPrompt, topicId, startDate, endDate]);

    if (!topicId) return null;

    return (
        <div className="llm-movers-section">
            <div className="llm-movers-header">
                <div>
                    <h3 className="llm-movers-title">Prompt Movers</h3>
                    <p className="llm-movers-subtitle">
                        Prompts whose answer changed between consecutive asks — a rating swing of {minDelta}+
                        or the brand entering/leaving the answer. Score impact is the exact points of that
                        day's (unsmoothed) daily score explained by the move.
                    </p>
                </div>
                <div className="llm-movers-controls">
                    <label className="llm-movers-delta">
                        Min swing
                        <select value={minDelta} onChange={e => setMinDelta(Number(e.target.value))}>
                            {DELTA_OPTIONS.map(d => <option key={d} value={d}>±{d}</option>)}
                        </select>
                    </label>
                    <div className="llm-movers-filters">
                        {TYPE_FILTERS.map(f => (
                            <button
                                key={f.key}
                                className={`llm-movers-filter ${typeFilter === f.key ? 'active' : ''}`}
                                onClick={() => setTypeFilter(f.key)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <CircularProgress size={26} />
                </div>
            ) : filtered.length === 0 ? (
                <div className="llm-movers-empty">
                    No prompt changed its answer by that much in this period — movement in the score
                    line is broad drift rather than specific prompts flipping.
                </div>
            ) : (
                <>
                    <table className="llm-movers-table">
                        <thead>
                            <tr>
                                <th>Day</th>
                                <th>Prompt</th>
                                <th>Change</th>
                                <th>Score impact</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, visible).map(m => {
                                const key = `${m.prompt_id}-${m.day}`;
                                const expanded = expandedKey === key;
                                return [
                                    <tr key={key} className={expanded ? 'expanded' : ''}>
                                        <td className="llm-mover-day">{fmtDay(m.day)}</td>
                                        <td className="llm-mover-prompt" onClick={() => toggleExpand(m)}>
                                            {m.prompt}
                                            {m.key_persona && <span className="llm-chip subtle">{m.key_persona}</span>}
                                        </td>
                                        <td><ChangeCell m={m} /></td>
                                        <td className="llm-mover-impact">
                                            {m.score_contribution != null
                                                ? `${Number(m.score_contribution) > 0 ? '+' : ''}${m.score_contribution} pts`
                                                : '—'}
                                        </td>
                                        <td className="llm-mover-actions">
                                            <button className="llm-mover-link" onClick={() => toggleExpand(m)}>
                                                {expanded ? 'Hide trend' : 'Trend'}
                                            </button>
                                            {onViewAnswers && (
                                                <button className="llm-mover-link" onClick={() => onViewAnswers(m)}>
                                                    Answers
                                                </button>
                                            )}
                                        </td>
                                    </tr>,
                                    expanded && (
                                        <tr key={`${key}-series`} className="llm-mover-series-row">
                                            <td colSpan={5}>
                                                {seriesLoading && !seriesByPrompt[m.prompt_id] ? (
                                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                                                        <CircularProgress size={22} />
                                                    </div>
                                                ) : (
                                                    <PromptSeriesChart rows={seriesByPrompt[m.prompt_id]} />
                                                )}
                                            </td>
                                        </tr>
                                    ),
                                ];
                            })}
                        </tbody>
                    </table>
                    {filtered.length > visible && (
                        <div className="llm-view-more-container">
                            <button className="llm-view-more-button" onClick={() => setVisible(v => v + INITIAL_VISIBLE)}>
                                Show more ({filtered.length - visible} remaining)
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default LLMPromptMovers;
